/** @prettier */
// Needed for redux devtools warning

/* eslint no-underscore-dangle: 0 */
import { fromJS } from 'immutable';
import { routerMiddleware as createRouterMiddleware } from 'react-router-redux';
import { applyMiddleware, compose, createStore } from 'redux';
import { combineReducers } from 'redux-immutable';
import createSagaMiddleware from 'redux-saga';

import { all, fork } from 'redux-saga/effects';

import {
  accountActivity,
  affiliates,
  application,
  betBuilder,
  betBuilderReport,
  bets,
  betslip,
  carousel,
  cashier,
  cashout,
  casinoControls,
  currentBetReport,
  customContactForm,
  dataLayer,
  depositLimits,
  event,
  eventParticipants,
  events,
  exchange,
  inlineBetslip,
  liveScores,
  lookups,
  market,
  marketRules,
  modal,
  multiples,
  multiplesReport,
  navigation,
  openOffers,
  popularMarkets,
  portfolio,
  positions,
  price,
  promoPage,
  promotions,
  quickLinks,
  relatedEvents,
  releaseFeedback,
  resetPassword,
  routing,
  runner,
  settledBets,
  staticContent,
  staticText,
  streaming,
  termsAndConditions,
  thirdPartyClients,
  transactions,
  twoFactorAuthentication,
  unsubmittedOffers,
  uploadDocs,
  user,
  userRegistration,
  userSession,
  vegas,
} from '@xanadu/ui-shared/modules';
import { initialiseApp } from '@xanadu/ui-shared/modules/application/actions';
import { createInitialState } from '@xanadu/ui-shared/modules/application/reducer';
import { setLanguage } from '@xanadu/ui-shared/modules/user/actions';

import createHistory from 'history/createBrowserHistory';

import { reducers, sagas } from './modules';

const history = createHistory();
const enableDevTools = process.env.NODE_ENV !== 'production' && window.__REDUX_DEVTOOLS_EXTENSION__;

// Options for the redux devtools can be specified here. If you do specify
// See http://extension.remotedev.io/docs/API/Arguments.html for options
const reduxDevToolsOptions = {};

if (Object.keys(reduxDevToolsOptions).length) {
  // eslint-disable-next-line no-console
  console.warn('Custom redux devtool options have been specified.', reduxDevToolsOptions);
}

const routerMiddleware = createRouterMiddleware(history);

const sagaMiddleware = createSagaMiddleware();
const enhancer = compose(
  applyMiddleware(routerMiddleware, sagaMiddleware),
  enableDevTools ? window.__REDUX_DEVTOOLS_EXTENSION__(reduxDevToolsOptions) : (f) => f
);

let store;
const defaultInitialState = fromJS({});

/**
 * Returns the state of the store. Not recommended to use in react. Added for Backbone compatibility. Used for currency.
 * @return {Immutable#map} the contents of the redux store
 */
const dangerouslyGetState = () => (store ? store.getState() : defaultInitialState);

/**
 * Returns the store. Not recommended to use in react.
 * Added for dispatching actions from normal functions in sagas. Used for Customer onboarding.
 * @return {Immutable#map} the contents of the redux store
 */
const dangerouslyGetStore = () => store;

function* rootSaga({
  isPreLaunch,
  site,
  brand,
  language,
  excludedSports,
  sagas: localSagas,
  settings,
  featuresFlags,
  customization,
  clientDefaultCurrency,
  clientSupportEmail,
  availableCurrencies,
  availableFeatures,
  isMultiplesEnabled,
  isBetBuilderEnabled,
  isCashoutEnabled,
  liveChatEnabled,
  ZENDESK_KEY,
  ZENDESK_WIDGET_TYPE,
}) {
  if (isPreLaunch) {
    yield all(
      [...(localSagas || [])].map((saga) =>
        fork(saga, {
          // Here to pass brand specific ACTIONS to external shared sagas
          brand,
        })
      )
    );
    return;
  }

  yield all(
    [
      // sagas = sagas located in /common/modules/*
      ...sagas,
      // localSagas = sagas specific to brand coming from the brand's app
      ...(localSagas || []),
      routing.sagas,
      accountActivity.sagas,
      application.sagas,
      user.sagas,
      userSession.sagas,
      twoFactorAuthentication.sagas,
      exchange.sagas,
      termsAndConditions.sagas,
      quickLinks.sagas,
      unsubmittedOffers.sagas,
      carousel.sagas,
      bets.sagas,
      betslip.sagas,
      dataLayer.sagas,
      affiliates.sagas,
      cashier.sagas,
      liveScores.sagas,
      lookups.sagas,
      resetPassword.sagas,
      staticContent.sagas,
      inlineBetslip.sagas,
      staticText.sagas,
      streaming.sagas,
      thirdPartyClients.sagas,
      navigation.sagas,
      event.sagas,
      events.sagas,
      relatedEvents.sagas,
      releaseFeedback.sagas,
      market.sagas,
      marketRules.sagas,
      price.sagas,
      uploadDocs.sagas,
      openOffers.sagas,
      depositLimits.sagas,
      currentBetReport.sagas,
      transactions.sagas,
      modal.sagas,
      portfolio.sagas,
      positions.sagas,
      userRegistration.sagas,
      promoPage.sagas,
      customContactForm.sagas,
      ...settledBets.sagas,
      ...promotions.sagas,
      ...(featuresFlags.casinoEnabled ? [vegas.sagas, casinoControls.sagas] : []),
      ...(isMultiplesEnabled ? [multiples.sagas, multiplesReport.sagas] : []),
      ...(isBetBuilderEnabled ? [betBuilder.sagas, betBuilderReport.sagas] : []),
      ...(isCashoutEnabled ? [cashout.sagas] : []),
    ].map((saga) =>
      fork(saga, {
        // Here to pass brand specific ACTIONS to external shared sagas
        site,
        brand,
        language,
        settings,
        featuresFlags,
        customization,
        excludedSports,
        clientDefaultCurrency,
        clientSupportEmail,
        availableCurrencies,
        availableFeatures,
        liveChatEnabled,
        ZENDESK_WIDGET_TYPE,
        ZENDESK_KEY,
      })
    )
  );
}

export default function configureStore({
  site,
  brand,
  language,
  isPreLaunch,
  settings = {},
  featuresFlags = {},
  customization = {},
  excludedSports = [],
  clientDefaultCurrency,
  clientSupportEmail,
  availableCurrencies,
  availableFeatures,
  defaultProduct,
  liveChatEnabled,
  ZENDESK_WIDGET_TYPE,
  ZENDESK_KEY,
  sagas: localSagas,
  reducers: localReducers,
}) {
  const isMultiplesEnabled = availableFeatures.includes('multiples');
  const isBetBuilderEnabled = availableFeatures.includes('betbuilder');
  const isCashoutEnabled = availableFeatures.includes('cashout');

  const reducer = combineReducers({
    local: combineReducers({
      // reducers = reducers located in /common/modules/*
      ...reducers,
      // localReducers = reducers specific to brand coming from the brand's app
      ...(localReducers || []),
    }),
    routing: routing.reducer,
    accountActivity: accountActivity.reducer,
    application: application.reducer,
    user: user.reducer,
    exchange: exchange.reducer,
    unsubmittedOffers: unsubmittedOffers.reducer,
    lookups: lookups.reducer,
    dataLayer: dataLayer.reducer,
    resetPassword: resetPassword.reducer,
    staticContent: staticContent.reducer,
    staticText: staticText.reducer,
    streaming: streaming.reducer,
    bets: bets.reducer,
    depositLimits: depositLimits.reducer,
    openOffers: openOffers.reducer,
    betslip: betslip.reducer,
    inlineBetslip: inlineBetslip.reducer,
    thirdPartyClients: thirdPartyClients.reducer,
    quickLinks: quickLinks.reducer,
    affiliates: affiliates.reducer,
    carousels: carousel.reducer,
    cashier: cashier.reducer,
    twoFactorAuthentication: twoFactorAuthentication.reducer,
    termsAndConditions: termsAndConditions.reducer,
    navigation: navigation.reducer,
    event: event.reducer,
    events: events.reducer,
    eventParticipants: eventParticipants.reducer,
    market: market.reducer,
    marketRules: marketRules.reducer,
    runner: runner.reducer,
    price: price.reducer,
    popularMarkets: popularMarkets.reducer,
    uploadDocs: uploadDocs.reducer,
    settledBets: settledBets.reducer,
    currentBetReport: currentBetReport.reducer,
    transactions: transactions.reducer,
    modal: modal.reducer,
    portfolio: portfolio.reducer,
    positions: positions.reducer,
    promoPage: promoPage.reducer,
    customContactForm: customContactForm.reducer,
    promotions: promotions.reducer,
    relatedEvents: relatedEvents.reducer,
    ...(featuresFlags.casinoEnabled
      ? {
          // eslint-disable-next-line indent-legacy
          vegas: vegas.reducer,
          // eslint-disable-next-line indent-legacy
          casinoControls: casinoControls.reducer,
          // eslint-disable-next-line indent-legacy
        }
      : {}),
    ...(isMultiplesEnabled
      ? { multiples: multiples.reducer, multiplesReport: multiplesReport.reducer }
      : {}),
    ...(isBetBuilderEnabled
      ? {
          // eslint-disable-next-line indent-legacy
          betBuilder: betBuilder.reducer,
          // eslint-disable-next-line indent-legacy
          betBuilderReport: betBuilderReport.reducer,
          // eslint-disable-next-line indent-legacy
        }
      : {}),
    ...(isCashoutEnabled
      ? {
          // eslint-disable-next-line indent-legacy
          cashout: cashout.reducer,
          // eslint-disable-next-line indent-legacy
        }
      : {}),
  });

  const initialState = fromJS({
    application: createInitialState(defaultProduct, {
      fixedOddsType: featuresFlags.fixedOddsType,
      fixedExchangeType: featuresFlags.fixedExchangeType,
      fixedCurrency: featuresFlags.fixedCurrency,
    }),
  });

  store = createStore(reducer, initialState, enhancer);

  if (process.env.NODE_ENV !== 'production') {
    window.store = store;
  }

  sagaMiddleware.run(rootSaga, {
    site,
    isPreLaunch,
    sagas: localSagas,
    brand,
    language,
    settings,
    featuresFlags,
    customization,
    excludedSports,
    clientDefaultCurrency,
    clientSupportEmail,
    availableCurrencies,
    availableFeatures,
    defaultProduct,
    isMultiplesEnabled,
    isBetBuilderEnabled,
    isCashoutEnabled,
    liveChatEnabled,
    ZENDESK_WIDGET_TYPE,
    ZENDESK_KEY,
  });

  store.dispatch(initialiseApp({ brand, site, clientDefaultCurrency }));

  store.dispatch(setLanguage(language));

  return store;
}

export { history, dangerouslyGetState, dangerouslyGetStore };
